/**
 * SSN structural validation.
 *
 * Rejects SSNs that are well-formed (9 digits) but structurally invalid per
 * documented Social Security Administration rules. Mirrors the rules used in
 * idme-verification (IVA). The Workflow Engine does no structural validation
 * server-side — it forwards the SSN to a provider for a records match — so this
 * guard catches obviously-invalid input before it costs a provider call.
 *
 * Returns an error code for the violated rule, or null if the SSN is valid.
 * Callers map the code to a localized message.
 */

export type SsnValidationError = "repeating" | "sequential" | "invalid";

// SSNs that were never issued but are commonly entered. (Woolworth's 1938
// wallet-promo SSN and the SSA-advertised example number.)
const KNOWN_INVALID = new Set(["078051120", "219099999"]);

/**
 * @param digits raw, unformatted SSN — expected to be exactly 9 digits
 */
export function validateSsn(digits: string): SsnValidationError | null {
  if (!/^\d{9}$/.test(digits)) return "invalid";

  // All nine digits identical (e.g. 111111111).
  if (/^(\d)\1{8}$/.test(digits)) return "repeating";

  // Strictly ascending or descending runs (e.g. 123456789, 987654321).
  if (isSequential(digits)) return "sequential";

  const area = digits.slice(0, 3);
  const group = digits.slice(3, 5);
  const serial = digits.slice(5);

  // Area number cannot be 000 or 666, and 900–999 is reserved for ITINs.
  if (area === "000" || area === "666" || area[0] === "9") return "invalid";
  // Group number cannot be 00.
  if (group === "00") return "invalid";
  // Serial number cannot be 0000.
  if (serial === "0000") return "invalid";

  if (KNOWN_INVALID.has(digits)) return "invalid";

  return null;
}

function isSequential(digits: string): boolean {
  let ascending = true;
  let descending = true;
  for (let i = 1; i < digits.length; i++) {
    const diff = digits.charCodeAt(i) - digits.charCodeAt(i - 1);
    if (diff !== 1) ascending = false;
    if (diff !== -1) descending = false;
  }
  return ascending || descending;
}
